import { complement, eqBy, path, pipe, prop, pathEq } from 'ramda';
import find from 'lodash/fp/find';
import {
  compose,
  lifecycle,
  withHandlers,
  withPropsOnChange,
  withState,
} from 'recompose';
import { connect } from 'react-redux';
import { isEmpty, get } from 'lodash';
import {
  activeShopCashTipsEnabled,
  activeShopGiftCardTipsEnabled,
  activeShopReaderTipsEnabled,
  activeShopWebreader,
} from '../../../auth/selectors';
import {
  getCurrentBarber,
  getHasMore,
  getNextBarber,
  getPreviousBarber,
  getProcessing,
  getTippingPayments,
  tips,
} from '../../../register/tipping/selectors';
import { getAvailableTips, getShouldApplyFlatTipTiers } from '../../../register/validated/selectors';
import {
  getHasGiftCardPayment,
  getIsGiftCardPayment,
  getPayments,
  getHasReaderPayment,
} from '../../../register/payment/reducer/payments/selectors';
import { EMPTY_PAYMENT_TYPE } from '../constants';
import {
  assignTip,
  cancelTipping,
  nextTippingBarber,
  previousTippingBarber,
  setProcessing,
  tippingDone,
} from '../../../register/tipping/actions';
import Tipping from '../components/Tipping';
import { getWebreaderPaymentStatus } from '../../../register/webReader/selector';

const disabledState = withState('disabled', 'setDisabled', false);

const selectPayment = compose(
  connect(state => ({
    payments: getTippingPayments(
      getPayments(state),
      tips(state),
      {
        cashTipsEnabled: activeShopCashTipsEnabled(state),
        giftCardTipsEnabled: activeShopGiftCardTipsEnabled(state),
        readerTipsEnabled: activeShopReaderTipsEnabled(state),
      }
    ),
    isReaderPayment: activeShopWebreader(state) && getHasReaderPayment(state),
  })),
  withState('paymentBalance', 'setPaymentBalance', null),
  withState(
    'selectedPayment',
    'setSelectedPayment',
    (props) => {
      if (props.payments.length === 1) {
        props.setPaymentBalance(props.payments[0].balance);
        return props.payments[0].id;
      }
      return EMPTY_PAYMENT_TYPE;
    }
  ),
  withHandlers({
    onPaymentChange: props => (e) => {
      const id = e.target.value;
      const balance = pipe(
        find({ id }),
        prop('balance')
      )(props.payments);
      props.setPaymentBalance(balance);
      props.setSelectedPayment(id);
    },
  }),
);

const setBarberTips = withState('availableBarberTips', 'setAvailableBarberTips', []);

const afterTipping = props => () => {
  if (props.hasMore) {
    props.setDisabled(false);
    props.nextTippingBarber();
  } else {
    props.setDisabled(false);
    props.setProcessing(true);
    props.tippingDone();
  }
};

const TIP_DELAY = 300;
const isEmptyPayment = paymentId => pipe(find({ id: paymentId }), pathEq(['empty'], true));

const handlers = compose(
  withHandlers({
    setAvailableTipsForBarber: props => (barberId) => {
      const barberTips = get(props, `availableTips.${barberId}`, []);

      if (props.shouldUseFlatTip) {
        props.setAvailableBarberTips(barberTips.map(tip => ({
          amount: tip.flatAmount,
        })));
      } else {
        props.setAvailableBarberTips(barberTips.map(tip => ({
          ...tip,
          label: `${tip.percentage}%`,
        })));
      }
    },
    setTip: props => (tipAmount) => {
      props.setDisabled(true);
      const paymentId = props.selectedPayment !== EMPTY_PAYMENT_TYPE || !isEmptyPayment(props.selectedPayment)(props.payments)
        ? props.selectedPayment : null;
      props.assignTip(
        props.currentBarber.id,
        props.currentBarber.firstName,
        paymentId,
        Math.ceil(tipAmount),
        isEmptyPayment(props.selectedPayment)(props.payments) && 'cash',
      );
      setTimeout(afterTipping(props), TIP_DELAY);
    },
    getTippingDisabled: props => amount => (
        props.isProcessing ||
        props.disabled ||
        (props.giftCardTipsDisabled && props.giftCardPaymentSelected) ||
        props.noPaymentSelected ||
        (Number.isFinite(props.paymentBalance) && amount > props.paymentBalance)
      ),
  }),
  withHandlers({
    payWithTip: props => tipAmount => () => {
      props.setTip(tipAmount);
    },
    payNoTip: props => () => {
      props.setTip(0);
    },
    previousClick: props => () => props.previousTippingBarber(),
    nextClick: props => () => props.nextTippingBarber(),
  })
);

const redirectWhenEmpty = lifecycle({
  componentDidMount() {
    if (isEmpty(this.props.currentBarber)) {
      this.props.cancelTipping();
    }
  },
});

const mapStateToProps = (state, ownProps) => ({
  availableTips: getAvailableTips(state),
  previousBarber: getPreviousBarber(state),
  currentBarber: getCurrentBarber(state),
  nextBarber: getNextBarber(state),
  hasMore: getHasMore(state),
  hasGiftCard: getHasGiftCardPayment(state),
  giftCardPaymentSelected: getIsGiftCardPayment(state, ownProps.selectedPayment),
  giftCardTipsDisabled: !activeShopGiftCardTipsEnabled(state),
  cashTipsEnabled: activeShopCashTipsEnabled(state),
  isProcessing: getProcessing(state),
  webreaderPaymentStatus: getWebreaderPaymentStatus(state),
  noPaymentSelected: ownProps.payments.length ? ownProps.selectedPayment === EMPTY_PAYMENT_TYPE : true,
  shouldUseFlatTip: getShouldApplyFlatTipTiers(state),
});

const mapDispatchToProps = {
  nextTippingBarber,
  assignTip,
  tippingDone,
  previousTippingBarber,
  cancelTipping,
  setProcessing,
};

export default compose(
  disabledState,
  selectPayment,
  setBarberTips,
  connect(mapStateToProps, mapDispatchToProps),
  handlers,
  redirectWhenEmpty,
  withPropsOnChange(
    complement(eqBy(path(['currentBarber', 'id']))),
    (props) => {
      props.setDisabled(false);
      props.setAvailableTipsForBarber(props.currentBarber.id);
      if (props.currentBarber.paymentId) {
        props.setSelectedPayment(props.currentBarber.paymentId);
        return;
      }
      if (props.payments.length > 1) {
        props.setSelectedPayment(EMPTY_PAYMENT_TYPE);
      }
    }
  ),
)(Tipping);
